"use client";

/**
 * The iOS design system for the native app.
 *
 * DESIGN CONTRACT — every /m page builds from these primitives so the app
 * reads as ONE piece of software:
 *
 *   • One accent: the brand primary. Everything else is grayscale; color is
 *     reserved for MEANING (green = money/connected, red = destructive/missed,
 *     amber = pending). If a screen is colorful, it is wrong.
 *   • No gradients, no shadow-lg, no glassmorphism. Cards are white on the
 *     iOS system-gray canvas (#F2F2F7) with hairline borders. Depth comes from
 *     the canvas/card contrast, exactly like Settings.app.
 *   • Numbers are the heroes. Big tabular-nums figures in near-black; labels
 *     are small, gray, and quiet. A stat never sits in a colored box.
 *   • Motion is feedback, not decoration: press-scale on touch, and nothing
 *     else. No staggered entrances, no springs on scroll.
 *   • Grouped inset lists for everything list-shaped: 16px side margins,
 *     hairline separators inset to align with text, chevrons only when a row
 *     navigates.
 *
 * WHY THIS EXISTS: the first pass shipped 27 gradient tiles across 8 screens —
 * the generic template look. This file is the single place the look lives; a
 * redesign is edits here, not a page-by-page hunt.
 */

import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/app/_module/lib/utils";

/* ── Haptics ─────────────────────────────────────────────────────────── */

export const haptic = (style: "light" | "medium" | "heavy" = "light") => {
  try {
    const nav = navigator as any;
    if (nav?.vibrate) nav.vibrate({ light: 8, medium: 16, heavy: 28 }[style]);
  } catch {
    /* not supported — fine */
  }
};

/* ── Pressable ───────────────────────────────────────────────────────── */

/** Touch feedback without framer-motion: scale + dim on active, GPU-cheap. */
export function Pressable({
  className,
  children,
  hapticStyle = "light",
  onClick,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { hapticStyle?: "light" | "medium" | "heavy" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        haptic(hapticStyle);
        onClick?.(e);
      }}
      className={cn(
        "transition-[transform,opacity] duration-100 active:scale-[0.97] active:opacity-80",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ── Screen scaffolding ──────────────────────────────────────────────── */

/** Page container: iOS canvas gray, safe bottom padding above the tab bar. */
export function Screen({ className, children, id }: { className?: string; children: React.ReactNode; id?: string }) {
  return (
    // min-h-[100dvh], not min-h-full: the scroll wrapper (PullToRefresh) has
    // an auto-height content box, so percentage min-heights resolve to nothing
    // and short pages ended mid-screen with a white band below. dvh measures
    // the viewport directly.
    // pb-4 only: the scroll wrapper's glass-dock-pad already clears the dock
    // (safe-area + 96px). Stacking pb-28 on top left ~200px of dead gray
    // under every page's last card.
    <div id={id} className={cn("m-bleed min-h-[100dvh] bg-[#F2F2F7] px-4 pb-4 pt-2", className)}>
      <div className="mx-auto w-full m-col">{children}</div>
    </div>
  );
}

/** iOS large-title: 34px/bold on iPhone. Subtitle is the quiet context line. */
export function LargeTitle({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-4 mt-2 flex items-end justify-between gap-3">
      <div className="min-w-0">
        {subtitle && (
          <p className="text-[13px] font-medium text-[#8E8E93]">{subtitle}</p>
        )}
        <h1 className="truncate text-[32px] font-bold leading-9 tracking-tight text-black">
          {title}
        </h1>
      </div>
      {right && <div className="flex-shrink-0 pb-1">{right}</div>}
    </div>
  );
}

/** Uppercase section label, exactly like grouped-table headers in iOS. */
export function SectionLabel({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="mb-1.5 mt-6 flex items-baseline justify-between px-4">
      <p className="text-[13px] font-medium uppercase tracking-wide text-[#8E8E93]">{children}</p>
      {right}
    </div>
  );
}

/* ── Inset grouped list ──────────────────────────────────────────────── */

/** The white rounded card that holds Rows. Hairline ring, no shadow. */
export function InsetGroup({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[10px] bg-white ring-1 ring-black/[0.04]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * One row of an inset group. Separator is drawn by the group via divide, so
 * use <InsetGroup className="divide-y divide-black/[0.06]">.
 */
export function Row({
  icon,
  iconBg,
  title,
  subtitle,
  value,
  chevron,
  onClick,
  right,
  destructive,
}: {
  icon?: React.ReactNode;
  /** Small iOS-style icon squircle, e.g. "bg-blue-500". Solid, never gradient. */
  iconBg?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  right?: React.ReactNode;
  destructive?: boolean;
}) {
  const Inner = (
    <div className="flex min-h-[46px] w-full items-center gap-3 px-4 py-2.5 text-left">
      {icon && (
        <span
          className={cn(
            "flex h-[29px] w-[29px] flex-shrink-0 items-center justify-center rounded-[7px] text-white [&>svg]:h-[17px] [&>svg]:w-[17px]",
            iconBg ?? "bg-[#8E8E93]",
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-[16px] leading-5", destructive ? "text-red-500" : "text-black")}>
          {title}
        </span>
        {subtitle && (
          <span className="block truncate text-[13px] leading-4 text-[#8E8E93]">{subtitle}</span>
        )}
      </span>
      {value && <span className="flex-shrink-0 text-[16px] tabular-nums text-[#8E8E93]">{value}</span>}
      {right}
      {chevron && <ChevronRight className="h-4 w-4 flex-shrink-0 text-[#C7C7CC]" strokeWidth={2.5} />}
    </div>
  );
  if (!onClick) return Inner;
  return (
    <Pressable onClick={onClick} className="block w-full active:bg-black/[0.04] active:!scale-100">
      {Inner}
    </Pressable>
  );
}

/* ── Stats ───────────────────────────────────────────────────────────── */

/**
 * A stat: the number is the design. White tile, big tabular figure, quiet
 * label, optional small trend. Never a colored box.
 */
export function StatTile({
  label,
  value,
  sub,
  trend,
  onClick,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  /** e.g. "+12%" — green when it starts with "+", red with "-". */
  trend?: string;
  onClick?: () => void;
  /** Tint the VALUE only (money = green). Default near-black. */
  accent?: "green" | "primary";
}) {
  const body = (
    <div className="flex h-full flex-col rounded-[10px] bg-white p-3.5 ring-1 ring-black/[0.04]">
      <p className="text-[12px] font-medium text-[#8E8E93]">{label}</p>
      <p
        className={cn(
          "mt-0.5 text-[26px] font-bold leading-8 tracking-tight tabular-nums",
          accent === "green" ? "text-[#34C759]" : accent === "primary" ? "text-primary" : "text-black",
        )}
      >
        {value}
      </p>
      <div className="mt-auto flex items-baseline gap-1.5 pt-0.5">
        {trend && (
          <span
            className={cn(
              "text-[12px] font-semibold tabular-nums",
              trend.startsWith("-") ? "text-red-500" : "text-[#34C759]",
            )}
          >
            {trend}
          </span>
        )}
        {sub && <span className="truncate text-[12px] text-[#8E8E93]">{sub}</span>}
      </div>
    </div>
  );
  if (!onClick) return body;
  return (
    <Pressable onClick={onClick} className="block h-full w-full text-left">
      {body}
    </Pressable>
  );
}

/* ── Segmented control ───────────────────────────────────────────────── */

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}) {
  // Desktop's TabsList look: a white pill with a 2px black border + shadow
  // that SLIDES between segments (same 200ms curve as ui/tabs.tsx). Equal-
  // width segments make the math trivial — no measuring needed.
  const idx = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div className="relative flex rounded-[10px] bg-black/[0.06] p-0.5">
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0.5 top-0.5 rounded-[8px] border-2 border-black bg-white shadow-md"
        style={{
          left: 2,
          width: `calc((100% - 4px) / ${options.length})`,
          transform: `translateX(${idx * 100}%)`,
          transition: "transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)",
        }}
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => {
            haptic();
            onChange(o.value);
          }}
          className={cn(
            "relative z-10 flex-1 rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition-colors duration-200",
            value === o.value ? "text-black" : "text-[#3C3C43]/60",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Chips (horizontal filter row) ───────────────────────────────────── */

export function Chip({
  active,
  onClick,
  children,
  count,
}: {
  active?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <Pressable
      onClick={onClick}
      className={cn(
        "flex flex-shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold",
        active ? "bg-black text-white" : "bg-white text-black ring-1 ring-black/[0.08]",
      )}
    >
      {children}
      {count !== undefined && (
        <span className={cn("tabular-nums", active ? "text-white/60" : "text-[#8E8E93]")}>
          {count}
        </span>
      )}
    </Pressable>
  );
}

/* ── Avatar ──────────────────────────────────────────────────────────── */

const AVATAR_TONES = [
  "bg-[#E9F0FF] text-[#3B62C4]",
  "bg-[#EAF7EE] text-[#2E7D4F]",
  "bg-[#FDF1E7] text-[#B26A2B]",
  "bg-[#F3EAFB] text-[#7A4FA3]",
  "bg-[#FDEAEA] text-[#B24040]",
  "bg-[#E8F6F8] text-[#2B7A85]",
];

/** Deterministic initials avatar — same person, same tone, every screen. */
export function InitialsAvatar({ name, size = 40 }: { name?: string | null; size?: number }) {
  const n = (name || "?").trim();
  const parts = n.split(/\s+/);
  const initials =
    parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : n.slice(0, 2);
  let hash = 0;
  for (const ch of n) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  const tone = AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length];
  return (
    <span
      className={cn("flex flex-shrink-0 items-center justify-center rounded-full font-semibold", tone)}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials.toUpperCase()}
    </span>
  );
}

/* ── Empty & loading ─────────────────────────────────────────────────── */

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-8 py-14 text-center">
      {icon && <div className="mb-3 text-[#C7C7CC] [&>svg]:h-9 [&>svg]:w-9">{icon}</div>}
      <p className="text-[16px] font-semibold text-black">{title}</p>
      {hint && <p className="mt-1 text-[14px] leading-5 text-[#8E8E93]">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function RowSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <InsetGroup className="divide-y divide-black/[0.06]">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <span className="h-10 w-10 animate-pulse rounded-full bg-black/[0.06]" />
          <span className="flex-1 space-y-1.5">
            <span className="block h-3.5 w-2/5 animate-pulse rounded bg-black/[0.06]" />
            <span className="block h-3 w-3/5 animate-pulse rounded bg-black/[0.05]" />
          </span>
        </div>
      ))}
    </InsetGroup>
  );
}

/* ── Status pill (lead stages, appointment states) ───────────────────── */

const STATUS_TONES: Record<string, string> = {
  green: "bg-[#EAF7EE] text-[#2E7D4F]",
  red: "bg-[#FDEAEA] text-[#C0392B]",
  amber: "bg-[#FDF4E7] text-[#B26A2B]",
  blue: "bg-[#E9F0FF] text-[#3B62C4]",
  gray: "bg-black/[0.05] text-[#6B6B70]",
};

export function StatusPill({ tone = "gray", children }: { tone?: keyof typeof STATUS_TONES; children: React.ReactNode }) {
  return (
    <span className={cn("inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_TONES[tone])}>
      {children}
    </span>
  );
}

/** Map a lead_stat string onto a pill tone without a 100-entry table. */
export function leadStatTone(stat?: string | null): keyof typeof STATUS_TONES {
  const s = (stat || "").toLowerCase();
  if (/sold|approved|issued|paid|won/.test(s)) return "green";
  if (/bad|dnc|dead|declin|lost|not interested/.test(s)) return "red";
  if (/appt|appointment|callback|follow|underwriting|pending/.test(s)) return "amber";
  if (/new/.test(s)) return "blue";
  return "gray";
}
